import { ReactNode, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TriangleAlert } from 'lucide-react'

import styles from './EnterpriseAccessManagement.module.scss'

import { isApiError } from '@/api'
import { WorkosDirectoryStatusResponse } from '@/api/generated'
import { updateWorkosDirectoryId } from '@/api/internal'
import Button from '@/components/Button'
import Input from '@/components/Input'
import { useToast } from '@/components/Toast/ToastContext'
import Tooltip from '@/components/Tooltip'
import { enterpriseDetailsQueryKey, useEnterpriseDetails } from '@/hooks/useEnterpriseDetails'
import { useWorkosDirectoryStatus, workosDirectoryStatusQueryKey } from '@/hooks/useWorkosDirectoryStatus'
import { DirectorySyncControl } from '@/pages/app/Enterprises/components/DirectorySyncSection'

interface EnterpriseAccessManagementProps {
  enterpriseId: string
  enterpriseName: string
}

const EnterpriseAccessManagement = ({ enterpriseId, enterpriseName }: EnterpriseAccessManagementProps) => {
  const { data: detailsResponse, isLoading } = useEnterpriseDetails(enterpriseId)
  const { data: directoryStatusResponse, isLoading: isDirectoryStatusLoading } = useWorkosDirectoryStatus(enterpriseId)
  const details = detailsResponse?.data
  const directoryStatus = directoryStatusResponse?.data
  const workosOrgId = details?.workos_org_id ?? null
  const workosConId = details?.workos_con_id ?? null
  const workosProvider = details?.workos_provider ?? null
  const workosDirectoryId = details?.workos_directory_id ?? null
  const ssoEnabled = workosConId !== null
  // Sync requires the DB-linked directory ID; the live WorkOS check must never un-gate it.
  const hasDirectoryId = workosDirectoryId !== null

  const workosDirectories = directoryStatus?.status === 'ok' ? (directoryStatus.directories ?? []) : null
  const trimmedDirectoryId = workosDirectoryId?.trim() || null
  const directoryFound = workosDirectories !== null && workosDirectories.length > 0
  const directoryIdMismatch =
    workosDirectories !== null &&
    trimmedDirectoryId !== null &&
    !workosDirectories.some((d) => d.id === trimmedDirectoryId)

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <span className={styles.label}>Access Management</span>
        <div className={styles.headerActions}>
          <DirectorySyncControl
            id={enterpriseId}
            name={enterpriseName}
            disabledReason={!isLoading && !hasDirectoryId ? 'Set a WorkOS Directory ID first' : undefined}
          />
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loading}>Loading…</div>
      ) : (
        <div className={styles.rows}>
          <Row label="SSO Enabled" value={<Badge yes={ssoEnabled} />} />
          <Row
            label="SCIM Directory"
            value={
              <DirectoryStatusBadge
                status={directoryStatus}
                isLoading={isDirectoryStatusLoading}
                dbDirectoryId={trimmedDirectoryId}
              />
            }
          />
          <Row label="WorkOS Organization ID" value={<IdValue value={workosOrgId} />} />
          <Row label="WorkOS Connection ID" value={<IdValue value={workosConId} />} />
          <Row label="WorkOS Provider" value={<IdValue value={workosProvider} />} />
          <Row
            label="WorkOS Directory ID"
            value={
              <span className={styles.editableValue}>
                {directoryIdMismatch && (
                  <WarningPill
                    text="Not found in WorkOS"
                    tooltip="This ID doesn't match any directory in this WorkOS organization. It may be stale or mistyped."
                  />
                )}
                <EditableDirectoryId
                  enterpriseId={enterpriseId}
                  value={workosDirectoryId}
                  missingWarning={directoryFound}
                />
              </span>
            }
          />
        </div>
      )}
    </div>
  )
}

const Row = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className={styles.row}>
    <span className={styles.rowLabel}>{label}</span>
    <span className={styles.rowValue}>{value}</span>
  </div>
)

const Badge = ({ yes }: { yes: boolean }) => (
  <span className={yes ? styles.badgeYes : styles.badgeNo}>{yes ? 'Yes' : 'No'}</span>
)

const DirectoryStatusBadge = ({
  status,
  isLoading,
  dbDirectoryId,
}: {
  status: WorkosDirectoryStatusResponse | undefined
  isLoading: boolean
  dbDirectoryId: string | null
}) => {
  if (isLoading) {
    return <span className={styles.loading}>Checking…</span>
  }
  const directories = (status?.status === 'ok' ? status.directories : null) ?? []
  if (directories.length === 0) {
    return <span className={styles.badgeWarning}>No directory found</span>
  }
  // Prefer the directory linked in our DB; fall back to the newest (WorkOS returns desc).
  const shown = directories.find((d) => d.id === dbDirectoryId) ?? directories[0]
  const countSuffix = directories.length > 1 ? `, ${directories.length} dirs` : ''
  return (
    <span className={shown.state === 'active' ? styles.badgeYes : styles.badgeWarning}>
      {`Yes (${shown.state}${countSuffix})`}
    </span>
  )
}

const WarningPill = ({ text, tooltip, severity }: { text: string; tooltip: string; severity?: 'error' }) => (
  <Tooltip text={tooltip} placement="top" hideArrow>
    <span className={severity === 'error' ? styles.errorPill : styles.missingPill}>
      <TriangleAlert size={14} aria-hidden /> {text}
    </span>
  </Tooltip>
)

const IdValue = ({ value }: { value: string | null }) =>
  value !== null ? (
    <span className={styles.idValue}>{value}</span>
  ) : (
    <span className={styles.missingPill}>
      <TriangleAlert size={14} aria-hidden /> Missing
    </span>
  )

const EditableDirectoryId = ({
  enterpriseId,
  value,
  missingWarning,
}: {
  enterpriseId: string
  value: string | null
  // Warn about the blank DB ID only when WorkOS actually has a directory to link.
  missingWarning: boolean
}) => {
  const queryClient = useQueryClient()
  const { updateToast } = useToast()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')

  const mutation = useMutation({
    mutationFn: (next: string | null) => updateWorkosDirectoryId(enterpriseId, next),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: enterpriseDetailsQueryKey(enterpriseId) })
      queryClient.invalidateQueries({ queryKey: workosDirectoryStatusQueryKey(enterpriseId) })
      updateToast('WorkOS Directory ID updated', 'success')
      setEditing(false)
    },
    onError: (error) => {
      const detail = isApiError(error) ? error.detail : undefined
      updateToast(detail || 'Failed to update WorkOS Directory ID', 'error')
    },
  })

  if (!editing) {
    return (
      <span className={styles.editableValue}>
        {value !== null ? (
          <span className={styles.idValue}>{value}</span>
        ) : missingWarning ? (
          <WarningPill
            text="Missing"
            severity="error"
            tooltip="A WorkOS directory exists but no Directory ID is saved. Set it to enable sync."
          />
        ) : (
          <span className={styles.notSet}>Not set</span>
        )}
        <Button
          title="Edit"
          type="light"
          onClick={() => {
            setDraft(value ?? '')
            setEditing(true)
          }}
        />
      </span>
    )
  }

  return (
    <span className={styles.editableValue}>
      <Input
        type="text"
        className={styles.editInput}
        value={draft}
        onInput={setDraft}
        placeholder="directory_…"
        isDisabled={mutation.isPending}
      />
      <Button
        title="Save"
        type="dark"
        isLoading={mutation.isPending}
        onClick={() => {
          const next = draft.trim() || null
          if (next === value) {
            setEditing(false)
            return
          }
          mutation.mutate(next)
        }}
      />
      <Button title="Cancel" type="light" isDisabled={mutation.isPending} onClick={() => setEditing(false)} />
    </span>
  )
}

export default EnterpriseAccessManagement
